import React, { useEffect, useMemo } from 'react'
import { injectIntl, FormattedMessage } from 'react-intl'
import { Flex, Col } from 'antd'
import { useCheckPermission } from '../hooks/Permissions'
import { useUsageMonitorPage } from '../hooks/UsageMonitorPage'
import {
  mergeIntervalsBySessionId,
  buildUserOptions,
  buildDateOptions,
  buildPlantOptions,
} from './utils'
import GroupedByUserGraph from './Widgets/GroupedByUserGraph'
import GroupedByPlantGraph from './Widgets/GroupedByPlantGraph'
import DailyActiveUsers from './Widgets/DailyActiveUsers'
import UserSessionTable from './Widgets/UserSessionTable'
import { requireAuthentication } from '../helpers/authentication'
import ReportPageLayout from './ReportPageLayout'
import ActiveFiltersDropdown from './Widgets/ActiveFiltersDropdown'

const LoggedUsersMonitorPage = injectIntl(() => {
  const checkPermission = useCheckPermission()
  const {
    plantFilter,
    setPlantFilter,
    userIdentifier,
    selectedUser,
    setSelectedUser,
    selectedDate,
    setSelectedDate,
    endTimeFilter,
    setStartTimeFilter,
    setEndTimeFilter,
    sortedData,
    setSortedData,
    lastUpdatedTime,
    setLastUpdatedTime,
    allUsageData,
    isLoading,
    refetch,
  } = useUsageMonitorPage()

  const plantOptions = useMemo(
    () => buildPlantOptions(sortedData, 'MONITOR_PAGE_LOADED'),
    [sortedData],
  )
  const userOptions = useMemo(
    () => buildUserOptions(sortedData, 'USER_LOG_OFF', userIdentifier),
    [sortedData, userIdentifier],
  )
  const dateOptions = useMemo(
    () => buildDateOptions(sortedData, 'USER_LOG_OFF'),
    [sortedData],
  )

  const handleBarClickOnPlantChart = (plant) => {
    if (!plant || plant === 'Unknown' || plant === plantFilter) {
      setPlantFilter(null)
    } else {
      setPlantFilter(plant)
    }
  }

  const handleBarClickOnUserChart = (user) => {
    if (!user || user === 'Unknown' || user === selectedUser) {
      setSelectedUser(null)
    } else {
      setSelectedUser(user)
    }
  }

  if (!checkPermission('page-usage-monitor:get')) {
    return (
      <div>
        <FormattedMessage
          id="loggedUserMonitor.accessDenied"
          defaultMessage="Access Denied: You do not have permission to view usage monitor."
        />
      </div>
    )
  }

  useEffect(() => {
    if (!isLoading && allUsageData) {
      setLastUpdatedTime(new Date())
      const sortedDataCopy = {}
      const mergedData = mergeIntervalsBySessionId(
        allUsageData,
        'MONITOR_PAGE_TIME_SPENT',
        'timeOnMonitorTable',
      )
      mergedData.forEach((data) => {
        if (sortedDataCopy[data.eventType]) {
          sortedDataCopy[data.eventType].push(data)
        } else {
          sortedDataCopy[data.eventType] = [data]
        }
      })
      setSortedData(sortedDataCopy)
    }
  }, [isLoading, allUsageData])

  return (
    <ReportPageLayout
      pageId="usage-monitor-page"
      breadcrumbLabel={{ id: 'loggedUserMonitor.header' }}
      breadcrumbSubLabel={{ id: 'loggedUserMonitor.subUsageLabel' }}
      onFilterChange={(start, end) => {
        setStartTimeFilter(start)
        setEndTimeFilter(end)
      }}
      onSync={() => refetch()}
      lastUpdatedTime={lastUpdatedTime}
    >
      {Object.keys(sortedData).length !== 0 && (
        <>
          <Flex
            vertical={false}
            gap={4}
            className="widget-row"
            justify="space-between"
          >
            <Col className="widget-container-wide">
              <Flex
                vertical={false}
                align="center"
                className="widget-title"
                justify="space-between"
              >
                <div>
                  <FormattedMessage
                    id="loggedUserMonitor.averageSessionDuration"
                    defaultMessage="Average Session Duration"
                  />
                </div>
              </Flex>
              <div className="widget-body">
                <GroupedByUserGraph
                  data={sortedData['USER_LOG_OFF']}
                  xFieldLabel={'Users'}
                  groupedValKey={'userTimeSpent'}
                  stat={'average'}
                  user_identifier={userIdentifier}
                  handleBarClick={handleBarClickOnUserChart}
                />
              </div>
            </Col>
            <Col className="widget-container-wide">
              <Flex
                vertical={false}
                align="center"
                className="widget-title"
                justify="space-between"
              >
                <div>
                  <FormattedMessage
                    id="loggedUserMonitor.dailyActiveSessions"
                    defaultMessage="Daily Active Sessions"
                  />
                </div>
              </Flex>
              <div className="widget-body">
                <DailyActiveUsers
                  data={sortedData['USER_LOG_OFF']}
                  endDate={endTimeFilter}
                  onDateSelect={setSelectedDate}
                />
              </div>
            </Col>
            <Col className="widget-container-wide">
              <Flex
                vertical={false}
                align="center"
                className="widget-title"
                justify="space-between"
              >
                <div>
                  <FormattedMessage
                    id="loggedUserMonitor.monitoringPageSessionsPerPlant"
                    defaultMessage="Monitoring Page Sessions Per Plant"
                  />
                </div>
              </Flex>
              <div className="widget-body">
                <GroupedByPlantGraph
                  data={sortedData['MONITOR_PAGE_LOADED']}
                  handleBarClick={handleBarClickOnPlantChart}
                />
              </div>
            </Col>
          </Flex>
          <Flex
            vertical={false}
            gap={4}
            className="widget-row"
            justify="space-between"
            style={{ height: 'auto', minHeight: '33%' }}
          >
            <Col span={24} className="widget-container-wide">
              <Flex
                vertical={false}
                align="center"
                className="widget-title"
                justify="space-between"
              >
                <div>
                  <FormattedMessage
                    id="loggedUserMonitor.userSessions"
                    defaultMessage="User Sessions"
                  />
                </div>
                <ActiveFiltersDropdown
                  filters={[
                    {
                      key: 'plant',
                      label: 'Plant',
                      value: plantFilter,
                      options: plantOptions,
                      onChange: setPlantFilter,
                    },
                    {
                      key: 'user',
                      label: 'User',
                      value: selectedUser,
                      options: userOptions,
                      onChange: setSelectedUser,
                    },
                    {
                      key: 'date',
                      label: 'Date',
                      value: selectedDate,
                      options: dateOptions,
                      onChange: setSelectedDate,
                    },
                  ]}
                />
              </Flex>
              <div className="widget-body">
                <UserSessionTable
                  data={allUsageData}
                  plantFilter={plantFilter}
                  selectedDate={selectedDate}
                  userIdentifier={userIdentifier}
                  selectedUser={selectedUser}
                />
              </div>
            </Col>
          </Flex>
        </>
      )}
    </ReportPageLayout>
  )
})

export default requireAuthentication(LoggedUsersMonitorPage)
